import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import mixpanel from 'mixpanel-browser';
import {
  ACTIVITY_LOGS_TABLE_COLUMNS,
  USER_ACTIVITY_LOG_PAGESIZE_OPTIONS,
} from '@app-user-management/constants/user-activity-log.constants';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UserActivityLogService } from '@app-user-management/services/user-activity-log/user-activity-log.service';
import { FieldChange, UserLogDetailsResponse, UsersLogDetailParams } from '@app-user-management/models/users.model';
import { DataService } from '@app-core/services/data/data.service';
import {
  ACTIVITY_LOG_CONFIGURATION_VALUE_MAPPING,
  ACTIVITY_LOG_SUB_FIELD_TRANSFORMATION,
  ADVANCED_SETTINGS_EXPANSION_PANEL_CONFIG,
  AUDIO_ALERTS,
  BASIC_SETTINGS_EXPANSION_PANEL_CONFIG,
  commonThresholds,
  CUSTOM_EVENTS_EXPANSION_PANEL_CONFIG,
  inheritConfigsMapping,
  nestedObjectInConfigsKeyMappings,
  PANIC_ACTION_MAPPINGS,
  PANIC_BUTTON_ACTIONS,
  PANIC_BUTTON_DISPLAY_VALUE,
  THRESHOLD_FIELDS,
} from '@app-asset-config/common/asset-configuration.constants';
import { TranslateService } from '@ngx-translate/core';
import { TAGGING_CONTROL_TYPES } from '@app-asset-config/constants/tagging.constants';
import { ENTITIES, EVENTS_CONFIG } from '@app-core/constants/constants';
import { AssetConfigurationService } from '@app-asset-config/services/asset-configuration.service';

const DUTY_TYPE_MAPPING = {
  heavy: 'Heavy',
  medium: 'Medium',
  light: 'Light',
  all: 'All',
  default: 'Default',
};

// we are not displaying these keys in the other details section and in the table
// but keeping these keys in data for easier debugging and future use
const RESTRICTED_ID_KEYS = ['attributeId', 'tagId', 'entityId', 'extAttributeId', 'rootTagId', 'driverId'];
const CONFIGURATION_CHANGE = [
  'FleetSdkCustomEventConfigurationsChange',
  'FleetSdkBasicConfigurationsChange',
  'FleetSdkAdvancedConfigurationsChange',
  'FleetSdkBasicConfigurationsDefault',
];

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.scss'],
})
export class ActivityLogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginator', { static: false })
  public paginator: MatPaginator;

  private logId: string;
  private searchInput = new FormControl('');
  public activityLogDataSource = new MatTableDataSource<any>([]);
  public activityLogTableColumns = ACTIVITY_LOGS_TABLE_COLUMNS;
  public pageSizeOptions = USER_ACTIVITY_LOG_PAGESIZE_OPTIONS;
  public tablePageSize = 5;
  public totalFields = 5;
  public loader: boolean = false;
  private offset = 0;
  public activityType: string = '';
  public user: string = '';
  public loginType: string = '';
  public timestamp: string = '';
  public timestampUTC: string = '';
  private fields: any = [];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private userActivityLogService: UserActivityLogService,
    public dataService: DataService,
    public translate: TranslateService,
    public assetConfigService: AssetConfigurationService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      this.logId = params.logId;
      this.getUserLogDetails();
    });

    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value && !this.loader) {
        this.getUserLogDetails();
      }
    });
  }

  ngAfterViewInit() {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.tablePageSize = event.pageSize;
      this.offset = event.pageIndex * this.tablePageSize;
      this.onSearch(this.searchInput.value, false);
    });
  }

  private getUserLogDetails() {
    this.loader = true;
    this.activityLogDataSource.data = new Array(this.tablePageSize).fill({});
    const params = new UsersLogDetailParams({
      logId: this.logId,
    });

    this.userActivityLogService
      .getUserActivityViewDetails(params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: UserLogDetailsResponse) => {
          this.activityType = this.userActivityLogService.getActivityLabelByType(res.activityType);
          this.fields = Array.isArray(res.fields)
            ? this.transformRawFields(res, res.fields)
                .map((field) => ({
                  ...field,
                  field: this.getFieldTitle(res, field),
                  subField: this.getSubFieldTitle(field),
                  oldValue: this.getDisplayValue(this.transformRawValue(res, field, field.oldValue)),
                  newValue: this.getDisplayValue(this.transformRawValue(res, field, field.newValue)),
                  activityMetadata: this.metaDataConverter(res, field.activityMetadata),
                }))
                .filter((field) => field.field)
            : [];

          this.timestamp = res.timestamp;
          this.timestampUTC = res.timestampUTC;
          this.user = res.userMetadata?.name || res.userMetadata?.userId || 'N/A';
          this.loginType = res.userMetadata?.loginType;

          this.onSearch(this.searchInput.value);
        },
        () => {
          this.activityLogDataSource.data = [];
        }
      );
  }

  private toCanonicalString(value) {
    if (value === null) {
      return 'null';
    }

    const type = typeof value;

    switch (type) {
      case 'string':
        return `string:${value}`;
      case 'number':
        return `number:${value}`;
      case 'boolean':
        return `boolean:${value}`;
      case 'undefined':
        return 'undefined';
      case 'object': {
        if (Array.isArray(value)) {
          // Array
          const items = value.map((item) => this.toCanonicalString(item));
          return `array:[${items.join(',')}]`;
        }

        // Object
        const keys = Object.keys(value).sort(); // sort keys for consistent ordering
        const keyValuePairs = keys.map((key) => `${key}:${this.toCanonicalString(value[key])}`);
        return `object:{${keyValuePairs.join(',')}}`;
      }
      case 'function':
        // Depending on your needs, you might skip or handle function differently
        return `function:${value.toString()}`;
      default:
        // Fallback for any unexpected types (e.g., symbol)
        return `${type}:${String(value)}`;
    }
  }

  private mergeDutyTypes(changes) {
    const merged = {};

    changes.forEach((change) => {
      const { dutyType, ...metaData } = change.activityMetadata;
      const key = `${this.toCanonicalString(change.field)}:${this.toCanonicalString(change.subField)}:${this.toCanonicalString(
        change.oldValue
      )}:${this.toCanonicalString(change.newValue)}:${this.toCanonicalString(metaData)}`;

      if (!merged[key]) {
        merged[key] = {
          field: change.field,
          oldValue: change.oldValue,
          newValue: change.newValue,
          subField: change.subField,
          activityMetadata: {
            ...metaData,
            dutyType: new Set(Array.isArray(dutyType) ? dutyType : [dutyType]),
          },
        };
      } else {
        dutyType.forEach((duty) => merged[key].activityMetadata.dutyType.add(duty));
      }
    });

    // Convert Sets back to Arrays
    return Object.values(merged).map((entry: any) => {
      const { dutyType: dutyTypeSet, ...activityMetadata } = entry.activityMetadata || {};
      let dutyType = Array.from(dutyTypeSet);
      const isAllDutyType = ['heavy', 'medium', 'light'].every((requiredDuty) => dutyType.includes(requiredDuty));

      return {
        ...(entry || {}),
        activityMetadata: {
          ...activityMetadata,
          dutyType: isAllDutyType ? ['all'] : dutyType,
        },
      };
    });
  }

  private transformRawFields(res: UserLogDetailsResponse, fields: FieldChange[]): FieldChange[] {
    const transformedFields: FieldChange[] = [];

    // Helper function to transform a field with nested object mapping.
    const transformNestedField = (field: FieldChange): FieldChange[] => {
      const mapping = nestedObjectInConfigsKeyMappings[field.field];
      // Build an inverted mapping: externalKey -> internalKey.
      const invertedMapping: { [externalKey: string]: string } = {};
      for (const internal in mapping) {
        if (mapping.hasOwnProperty(internal)) {
          const external = mapping[internal];
          invertedMapping[external] = internal;
        }
      }

      const oldValues = field.oldValue || {};
      const newValues = field.newValue || {};
      const keys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
      const nestedFields: FieldChange[] = [];

      for (const key of keys) {
        const transformedFieldKey = invertedMapping[key];
        if (!transformedFieldKey) {
          continue;
        }
        const oldVal = oldValues[key];
        const newVal = newValues[key];
        if (oldVal !== newVal) {
          nestedFields.push({
            field: transformedFieldKey,
            oldValue: oldVal,
            newValue: newVal,
            activityMetadata: field.activityMetadata,
          });
        }
      }
      return nestedFields;
    };

    for (const field of fields) {
      if (RESTRICTED_ID_KEYS.includes(field.field)) {
        continue;
      }

      // Create a shallow copy to avoid mutating the original field.
      const newField = { ...field };

      // Transform subField using the ACTIVITY_LOG_SUB_FIELD_TRANSFORMATION mapping.
      for (const matcher in ACTIVITY_LOG_SUB_FIELD_TRANSFORMATION) {
        if (ACTIVITY_LOG_SUB_FIELD_TRANSFORMATION.hasOwnProperty(matcher)) {
          const subFieldValue = ACTIVITY_LOG_SUB_FIELD_TRANSFORMATION[matcher];
          if (newField.field.toLocaleLowerCase().includes(matcher)) {
            const { found } = this.geInheritedConfigEventField(newField.field);
            if (found) {
              newField.subField = subFieldValue;
            }
          }
        }
      }

      // If the field has a nested object mapping, transform its nested keys.
      // The original field will not be included in the final output.
      if (nestedObjectInConfigsKeyMappings[newField.field]) {
        transformedFields.push(...transformNestedField(newField));
        continue;
      }

      const getOldValue = (fieldName) => fields.find((field) => field.field === fieldName)?.oldValue;
      const getNewValue = (fieldName) => fields.find((field) => field.field === fieldName)?.newValue;

      if (['customEventVideoEnabled', 'customEventImageEnabled'].includes(newField.field)) {
        const isVideo = newField.field === 'customEventVideoEnabled';
        const oppositeField = isVideo ? 'customEventImageEnabled' : 'customEventVideoEnabled';

        if (newField.newValue) {
          newField.field = 'mediaType';
          newField.oldValue = getOldValue(oppositeField) ? (isVideo ? 'Image' : 'Video') : 'None';
          newField.newValue = isVideo ? 'Video' : 'Image';
          transformedFields.push(newField);
        } else if (!getNewValue(oppositeField)) {
          newField.field = 'mediaType';
          newField.oldValue = getOldValue('customEventVideoEnabled') ? 'Video' : getOldValue('customEventImageEnabled') ? 'Image' : 'None';
          newField.newValue = 'None';
          transformedFields.push(newField);
        }
      }

      // Otherwise, include the transformed field.
      transformedFields.push(newField);
    }

    if (CONFIGURATION_CHANGE.includes(res.activityType)) {
      return this.mergeDutyTypes(transformedFields);
    }

    return transformedFields;
  }

  public onSearch(key: string, resetPagination = true) {
    this.searchInput.setValue(key);

    this.track(`Search User - Activity Log Tab / Users Page`, {
      'Search Value': key,
    });

    const keyUC = (key || '').trim().toUpperCase();
    const filteredResults = this.fields.filter((f) => JSON.stringify(f).toUpperCase().includes(keyUC));

    if (resetPagination) {
      this.offset = 0;
      this.paginator.pageIndex = 0;
    }

    // Update totalFields to reflect the filtered results
    this.totalFields = filteredResults.length;

    // Update the data source with the current page slice
    this.activityLogDataSource.data = filteredResults.slice(this.offset, this.offset + this.tablePageSize);
  }

  public clearInput() {
    this.offset = 0;
    this.paginator.pageIndex = 0;
    this.searchInput.setValue('');
  }

  public isObject(val) {
    if (val === null) {
      return false;
    }
    return typeof val === 'function' || typeof val === 'object';
  }

  public navigateBack() {
    this.location.back();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getFieldTitle(res: UserLogDetailsResponse, field: FieldChange) {
    if (res.activityType === 'FleetSdkCustomEventConfigurationsChange') {
      return this.getCustomSettingsEventTitle(field);
    }

    if (CONFIGURATION_CHANGE.includes(res.activityType)) {
      return (
        this.getBasicSettingsEventTitle(field.field) ||
        this.getAdvancedSettingsEventTitle(field.field) ||
        this.getInheritedConfigsMappingEventTitle(field.field) ||
        this.getCustomSettingsEventTitle(field)
      );
    }

    return this.translate.instant(field.field);
  }

  private getSubFieldTitle(field: FieldChange) {
    const getKey = this.getKeyFromArrayFactory(field.subField);
    if (field.field === 'audioAlertsEnabled' && field.subField) {
      return this.translate.instant(getKey(AUDIO_ALERTS));
    }

    if (field.field === 'eventTypes' && field.subField) {
      if (EVENTS_CONFIG[field.subField]) {
        return this.translate.instant(EVENTS_CONFIG[field.subField].label);
      }

      for (const key in EVENTS_CONFIG) {
        if (EVENTS_CONFIG[key].childConfigs) {
          for (const childKey in EVENTS_CONFIG[key].childConfigs) {
            if (childKey === field.subField) {
              return this.translate.instant(EVENTS_CONFIG[key].childConfigs[childKey].label);
            }
          }
        }
      }
    }

    if (field.field.toLowerCase().includes('speeding')) {
      return this.translate.instant('Speeding');
    }

    if (!field.subField) {
      return null;
    }

    return this.translate.instant(field.subField);
  }

  private getKeyFromArrayFactory(value: string) {
    return (arr) => arr.find((option) => option.key === value)?.value || value;
  }

  private transformRawValue(res: UserLogDetailsResponse, field: FieldChange, value: any) {
    if (value === null || value === undefined) {
      return value;
    }

    if (
      [
        'FleetAttributeAdded',
        'FleetAttributeChanged',
        'FleetAttributeDeleted',
        'FleetTagAdded',
        'FleetTagChanged',
        'FleetTagDeleted',
      ].includes(res.activityType)
    ) {
      switch (field.field) {
        case 'attributeType':
          return TAGGING_CONTROL_TYPES.find((type) => type.value === value)?.Label;
        case 'entityName':
          return ENTITIES[value].label;
        case 'type':
          return TAGGING_CONTROL_TYPES.find((type) => type.value === value)?.Label;
        case 'entities':
          return value.map((entity) => ENTITIES[entity].label);
      }
    }

    if (PANIC_BUTTON_ACTIONS.includes(field.field)) {
      for (const key in PANIC_ACTION_MAPPINGS) {
        if (this.toCanonicalString(value) === this.toCanonicalString(PANIC_ACTION_MAPPINGS[key])) {
          return this.getKeyFromArrayFactory(key)(PANIC_BUTTON_DISPLAY_VALUE);
        }
      }
    }

    if (CONFIGURATION_CHANGE.includes(res.activityType)) {
      const specialValue = this.assetConfigService.convertSpecialValue(field.field, value);

      if (THRESHOLD_FIELDS.includes(field.field)) {
        return commonThresholds.find((threshold) => threshold.key === specialValue)?.metricValue || '-';
      }

      const getKey = this.getKeyFromArrayFactory(specialValue);

      for (const key in ACTIVITY_LOG_CONFIGURATION_VALUE_MAPPING.exact) {
        if (field.field === key) {
          return getKey(ACTIVITY_LOG_CONFIGURATION_VALUE_MAPPING.exact[key]);
        }
      }

      const label = getKey(ACTIVITY_LOG_CONFIGURATION_VALUE_MAPPING.includes);
      if (label) {
        return label;
      }

      return specialValue;
    }

    return value;
  }

  private getDisplayValue(value: any) {
    if (value === null) {
      return ['-'];
    }

    const displayValues = [];

    switch (typeof value) {
      case 'symbol':
      case 'bigint':
      case 'number': {
        displayValues.push(String(value));
        break;
      }

      case 'string': {
        displayValues.push(this.translate.instant(value || '-'));
        break;
      }

      case 'undefined': {
        displayValues.push('-');
        break;
      }

      case 'boolean': {
        if (value === false) {
          displayValues.push(this.translate.instant('Disabled'));
        } else {
          displayValues.push(this.translate.instant('Enabled'));
        }

        break;
      }

      case 'function': {
        break;
      }

      case 'object': {
        if (Array.isArray(value)) {
          for (const v of value) {
            displayValues.push(...this.getDisplayValue(v));
          }

          break;
        } else {
          if (value.hasOwnProperty('enabled')) {
            displayValues.push(...this.getDisplayValue(value.enabled));
            break;
          }

          // handle other object types
        }
      }
    }

    return displayValues;
  }

  private metaDataConverter(res: UserLogDetailsResponse, obj) {
    let metaData = { ...(obj || {}) };
    if (res.activityType === 'FleetSdkCustomEventConfigurationsChange') {
      delete metaData.eventType;
    }

    if (metaData.dutyType) {
      metaData.dutyType = Array.isArray(metaData.dutyType)
        ? metaData.dutyType.map((dutyType) => this.translate.instant(DUTY_TYPE_MAPPING[dutyType])).join(', ')
        : this.translate.instant(DUTY_TYPE_MAPPING[metaData.dutyType]);
    }

    if (metaData.entityName) {
      metaData.entityName = ENTITIES[metaData.entityName].label;
    }

    for (const restrictedKey of RESTRICTED_ID_KEYS) {
      if (metaData.hasOwnProperty(restrictedKey)) {
        delete metaData[restrictedKey];
      }
    }

    return Object.keys(metaData).map((key) => [key, metaData[key]]);
  }

  private getSettingsEventTitle(config: any, key: string) {
    const searchKey = (obj: any): string | null => {
      if (obj.key === key) {
        return this.translate.instant(obj.title || obj.placeholder);
      }

      for (const content of obj.content) {
        if (content.key === key) {
          return `${this.translate.instant(obj.title)} / ${this.translate.instant(content.placeholder)}`;
        }
      }

      for (const description of obj.description) {
        if (description.key === key) {
          return this.translate.instant(obj.title || obj.placeholder);
        }
      }

      for (const content of obj.additionalContent?.items || []) {
        if (content.key === key) {
          return `${this.translate.instant(obj.title)} / ${this.translate.instant(content.title)}`;
        }
      }

      if (obj.internalPanel) {
        for (const subKey in obj.internalPanel) {
          const foundTitle = searchKey(obj.internalPanel[subKey]);
          if (foundTitle) return this.translate.instant(foundTitle);
        }
      }

      return null;
    };

    for (const category in config) {
      const title = searchKey(config[category]);
      if (title) return title;
    }

    return null;
  }

  private getBasicSettingsEventTitle(key: string) {
    const config = BASIC_SETTINGS_EXPANSION_PANEL_CONFIG('');
    return this.getSettingsEventTitle(config, key);
  }

  private geInheritedConfigEventField(key: string) {
    for (const eventKey in inheritConfigsMapping) {
      const internalKey = inheritConfigsMapping[eventKey];
      if ([internalKey.key, internalKey.key2, internalKey.key3, internalKey.key4, internalKey.key5].includes(key)) {
        return {
          eventKey,
          found: true,
        };
      }
    }

    return {
      eventKey: null,
      found: false,
    };
  }

  private getInheritedConfigsMappingEventTitle(key: string) {
    const { found, eventKey } = this.geInheritedConfigEventField(key);
    if (found) {
      return this.getBasicSettingsEventTitle(eventKey);
    }

    return null;
  }

  private getAdvancedSettingsEventTitle(key: string) {
    return this.getSettingsEventTitle(ADVANCED_SETTINGS_EXPANSION_PANEL_CONFIG, key);
  }

  private getCustomSettingsEventTitle(field: FieldChange) {
    if (!field.activityMetadata.hasOwnProperty('eventType')) return null;

    const activityMetadata = field.activityMetadata as { eventType: string };
    if (!activityMetadata.eventType) return null;

    const config = CUSTOM_EVENTS_EXPANSION_PANEL_CONFIG({ [`eventType`]: activityMetadata.eventType });
    return this.getSettingsEventTitle(config, `${activityMetadata.eventType}${field.field}`);
  }

  track(...args: any[]) {
    try {
      mixpanel.track(...args);
    } catch (e) {
      // sometimes mixpanel throws error
      // just to ignore errors
    }
  }
}
